import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ratioToPercentage, toTimeDisplay } from "../../utils/mediaUtils";
import {
  MediaControl,
  MediaSpan,
  MediaTimeLine,
  PanoMediaControlsContainer,
  VolumeSlider,
} from "./PanoStyles";
import { ReactComponent as PauseIcon } from "../../images/pause-button.svg";
import { ReactComponent as PlayIcon } from "../../images/play-arrow.svg";
import { ReactComponent as MutedIcon } from "../../images/mute.svg";
import { ReactComponent as UnmutedIcon } from "../../images/unmute.svg";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage, useSessionStorage } from "../../hooks/useStorage";
import { useInterfaceState } from "../../InterfaceContext";
import { useKrpano } from "./Krpano";
import { Krpano } from "../../libs/krpano";
import { Flex } from "../../shared/components/Flex";
import { makeKeyDownHandler } from "../../utils/a11y-utils";
import { Pano } from "../../types";

interface Props {
  pano: Pano;
}

const muteKey = "muteMedia";
const mediaVolumeKey = "mediaVolume";

const volumeStepSize = 0.05;

export const PanoAudioControls = ({ pano }: Props) => {
  const { slug, audio, repeatAudio } = pano;

  const krpano = useKrpano();
  const [currentTime, setCurrentTime] = useState<number>();
  const [totalTime, setTotalTime] = useState(0);
  const [currentAudio, setCurrentAudio] = useState<Krpano.Sound | null>(
    krpano?.get(`sound[${slug}]`) || null
  );
  const [mutedRaw, setMutedRaw] = useSessionStorage(muteKey);
  const [volumeRaw, setVolumeRaw] = useLocalStorage(mediaVolumeKey);
  const intl = useIntl();
  const { isInVr } = useInterfaceState();
  const volumeSliderRef = useRef<HTMLInputElement>(null);

  const muted = useMemo(() => (mutedRaw ? JSON.parse(mutedRaw) : false), [mutedRaw]);
  const volume = useMemo(() => (volumeRaw ? JSON.parse(volumeRaw) : 0.5), [volumeRaw]);

  const setVolume = useCallback(
    (value: number) => setVolumeRaw(JSON.stringify(value)),
    [setVolumeRaw]
  );

  const setMuted = useCallback(
    (value: boolean) => setMutedRaw(JSON.stringify(value)),
    [setMutedRaw]
  );

  const destructor = useCallback(() => {
    krpano?.call("stopallsounds()");
  }, [krpano]);

  useEffect(() => destructor, [destructor]);

  useEffect(() => {
    if (!currentAudio?.DATA) return;

    // prevent destruction of the audio object after playback ends
    currentAudio.DATA.hsound._onend.splice(0);

    const waitForDuration = async () => {
      let interval = 0;

      const promise = new Promise<number>((resolve) => {
        interval = window.setInterval(() => {
          if (currentAudio.duration) {
            resolve(currentAudio.duration);
          }
        });
      });

      const duration = await promise;

      window.clearInterval(interval);

      return duration;
    };

    if (!currentAudio.duration) {
      currentAudio.muted = true;
      currentAudio.volume = volume;
      currentAudio.play();
      waitForDuration().then((duration) => {
        if (!currentAudio.loop) currentAudio.pause();
        currentAudio.muted = muted;
        setCurrentTime(currentAudio.position);
        setTotalTime(duration);
      });
    }
  }, [currentAudio, repeatAudio, muted, volume]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentTime((current) => {
        return currentAudio?.position ?? current;
      });
    }, 100);

    return () => window.clearInterval(interval);
  }, [currentAudio]);

  useEffect(() => {
    if (currentAudio) currentAudio.volume = volume;
  }, [currentAudio, volume]);

  useEffect(() => {
    if (currentAudio) currentAudio.muted = muted;
  }, [currentAudio, muted]);

  useEffect(() => {
    if (currentAudio) currentAudio.loop = repeatAudio;
  }, [currentAudio, repeatAudio]);

  useEffect(() => {
    setCurrentAudio((current) => {
      if (current?.name !== slug && !krpano?.get(`sound[${slug}]`))
        krpano?.call(`createsound(${slug}, ${audio}, false)`);

      return krpano?.get(`sound[${slug}]`) ?? null;
    });

    return () => {
      if (currentAudio?.paused === false) currentAudio?.stop();
    };
  }, [slug, audio, krpano]);

  const togglePlaying = useCallback(() => {
    if (!currentAudio) return;

    currentAudio.toggle();
  }, [currentAudio]);

  const seek = useCallback(
    (event: SyntheticEvent<HTMLElement, MouseEvent>) => {
      if (!currentAudio) return;

      const ratio = event.nativeEvent.offsetX / (event.target as HTMLElement).clientWidth;
      currentAudio.position = ratio * currentAudio.duration;
    },
    [currentAudio]
  );

  const scrub = useCallback(
    (event: SyntheticEvent<HTMLElement, KeyboardEvent>) => {
      if (!currentAudio) return;

      const delta =
        event.nativeEvent.ctrlKey || event.nativeEvent.metaKey
          ? 60
          : event.nativeEvent.shiftKey
          ? 10
          : 1;

      switch (event.nativeEvent.key) {
        case " ":
        case "Enter":
          event.preventDefault();
          togglePlaying();
          break;
        case "ArrowLeft":
          const targetLeft = Math.max(Number.MIN_VALUE, currentAudio.position - delta); // setting to `0` is ignored, so we'll take the smallest possible decimal
          currentAudio.position = targetLeft;
          break;
        case "ArrowRight":
          const targetRight = Math.min(currentAudio.duration, currentAudio.position + delta);
          currentAudio.position = targetRight;
          break;
      }
    },
    [currentAudio, togglePlaying]
  );

  const toggleMute = useCallback(() => {
    if (!currentAudio) return;

    if (currentAudio.volume === 0 && !currentAudio.muted) {
      setVolume(0.5);
    } else {
      setMuted(!currentAudio.muted);
    }
  }, [currentAudio, setMuted, setVolume]);

  const updateVolume = useCallback(
    (value: number) => {
      if (!currentAudio) return;

      if (currentAudio.muted && value) {
        setMuted(false);
      }
      setVolume(value);
    },
    [currentAudio, setMuted, setVolume]
  );

  const controlVolume = useCallback(
    (event: SyntheticEvent<HTMLElement, KeyboardEvent>) => {
      if (!currentAudio) return;

      switch (event.nativeEvent.key) {
        case " ":
        case "Enter":
          event.preventDefault();
          toggleMute();
          break;
        case "ArrowLeft":
          if (!volumeSliderRef.current) return;
          volumeSliderRef.current.focus();
          updateVolume(Math.max(0, volumeSliderRef.current.valueAsNumber - volumeStepSize));
          break;
        case "ArrowRight":
          if (!volumeSliderRef.current) return;
          volumeSliderRef.current.focus();
          updateVolume(Math.min(1, volumeSliderRef.current.valueAsNumber + volumeStepSize));
          break;
      }
    },
    [currentAudio, toggleMute, scrub]
  );

  const unmuteLabel = intl.formatMessage({
    id: "pano.media.unmute-aria-label",
    defaultMessage: "Turn off sound",
    description: "ARIA label for the mute button while the media is currently muted.",
  });
  const muteLabel = intl.formatMessage({
    id: "pano.media.mute-aria-label",
    defaultMessage: "Turn on sound",
    description: "ARIA label for the mute button while the media is not currently muted.",
  });
  const pauseLabel = intl.formatMessage({
    id: "pano.media.pause-aria-label",
    defaultMessage: "Pause",
    description: "ARIA label for the pause button.",
  });
  const playLabel = intl.formatMessage({
    id: "pano.media.play-aria-label",
    defaultMessage: "Play",
    description: "ARIA label for the play button.",
  });

  if (isInVr) return null;

  return (
    currentAudio && (
      <PanoMediaControlsContainer>
        <Flex column gap=".2em">
          {!currentAudio.loop && (
            <MediaTimeLine onClick={seek} tabIndex={0} onKeyDown={scrub}>
              <div
                style={{
                  width: `${ratioToPercentage(currentTime ?? 0, totalTime)}%`,
                }}
              />
            </MediaTimeLine>
          )}
          <Flex style={{ alignItems: "center", padding: "0 0.5rem" }}>
            <MediaControl
              onClick={togglePlaying}
              onKeyDown={makeKeyDownHandler(togglePlaying)}
              tabIndex={0}
            >
              {currentAudio.paused ? (
                <PlayIcon title={playLabel} aria-label={playLabel} />
              ) : (
                <PauseIcon title={pauseLabel} aria-label={pauseLabel} />
              )}
            </MediaControl>
            <Flex>
              <MediaControl onClick={toggleMute} onKeyDown={controlVolume} tabIndex={0}>
                {currentAudio.muted || !currentAudio.volume ? (
                  <MutedIcon title={unmuteLabel} aria-label={unmuteLabel} />
                ) : (
                  <UnmutedIcon title={muteLabel} aria-label={muteLabel} />
                )}
              </MediaControl>
              <VolumeSlider
                ref={volumeSliderRef}
                type="range"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateVolume(e.target.valueAsNumber)
                }
                value={muted ? 0 : volume}
                step={0.05}
                max={1}
              />
            </Flex>
            {!currentAudio.loop && (
              <MediaSpan>
                {toTimeDisplay(currentTime ?? 0)} / {toTimeDisplay(totalTime)}
              </MediaSpan>
            )}
            <MediaSpan>
              <FormattedMessage
                id="pano.media-type.audio"
                defaultMessage="Audio"
                description="Label depicting the currently playing media is a panoramic audio."
              />
            </MediaSpan>
          </Flex>
        </Flex>
      </PanoMediaControlsContainer>
    )
  );
};
